'use client';

import { useRef, useState } from 'react';
import { Button, ExpandableCallout, SingleSelect } from '@galapagos/design-system/web';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useSignupApi } from '../../hooks/useSignupApi';
import { useSignupNavigation } from '../../hooks/useSignupNavigation';
import { useStepHeading } from '../../hooks/useStepHeading';
import {
  SIGNUP_LANGUAGE_PREFERENCES,
  type SignupLanguagePreference,
} from '../../services/signup-api/signup-api';
import { PrivateFields } from '../../components/PrivateFields';
import { gated } from './gated';
import { WrapUpActions, WrapUpFrame } from './WrapUpFrame';

/** The catalog key naming each interpreter choice. */
const LANGUAGE_LABEL_KEYS = {
  en: 'wrapUp.languagePreference.english',
  es: 'wrapUp.languagePreference.spanish',
  ht: 'wrapUp.languagePreference.haitianCreole',
  other: 'wrapUp.languagePreference.other',
} as const satisfies Record<SignupLanguagePreference, string>;

function isLanguagePreference(value: string): value is SignupLanguagePreference {
  return SIGNUP_LANGUAGE_PREFERENCES.some((language) => language === value);
}

/**
 * `/language-preference/`: whether the member wants an interpreter on
 * their call. The choice is saved best effort: booking follows either way, as
 * in the legacy flow, so a failed save never strands the member.
 */
function LanguagePreferenceStep() {
  const { t } = useTranslation('signup');
  const title = t('wrapUp.languagePreference.title');
  const headingRef = useStepHeading(title);
  const api = useSignupApi();
  const { goToStep } = useSignupNavigation();
  const [language, setLanguage] = useState<SignupLanguagePreference>('en');
  const inFlight = useRef(false);
  const save = useMutation({
    mutationFn: (choice: SignupLanguagePreference) =>
      api.saveLanguagePreference({ language: choice }),
    onSettled: () => goToStep('booking'),
  });
  const onContinue = () => {
    if (inFlight.current) return;
    inFlight.current = true;
    save.mutate(language);
  };
  const options = SIGNUP_LANGUAGE_PREFERENCES.map((value) => ({
    value,
    label: t(LANGUAGE_LABEL_KEYS[value]),
  }));
  return (
    <WrapUpFrame title={title} headingRef={headingRef}>
      <ExpandableCallout
        title={t('wrapUp.languagePreference.whyTitle')}
        actionId="signup.language-preference-why.toggle"
        data-testid="signup-language-preference-why-callout"
      >
        <p className="signup-step__text">{t('wrapUp.languagePreference.whyBody')}</p>
      </ExpandableCallout>
      {/* The interpreter language says something about the member: no
          autocapture or RUM click from this choice. */}
      <PrivateFields>
        <SingleSelect
          label={t('wrapUp.languagePreference.groupLabel')}
          hideLabel
          actionId="signup.language-preference.change"
          data-testid="signup-language-preference-options"
          options={options}
          value={language}
          onSelect={(value) => {
            if (isLanguagePreference(value)) setLanguage(value);
          }}
        />
      </PrivateFields>
      <WrapUpActions status={save.isPending ? t('wrapUp.languagePreference.saving') : ''}>
        <Button
          size="L"
          type="Primary"
          label={t('wrapUp.languagePreference.continue')}
          actionId="signup.language-preference-continue.activate"
          data-testid="signup-language-preference-continue-button"
          state={save.isPending ? 'Loading' : 'Default'}
          onPress={onContinue}
        />
      </WrapUpActions>
    </WrapUpFrame>
  );
}

export const LanguagePreferenceScreen = gated(LanguagePreferenceStep, 'language-preference');
